// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { useLocation } from 'react-router-dom';
import { ReportSelectOption } from './LedgerStatementReport';

const defaultProjectOption: ReportSelectOption = { label: 'Select Project', value: '' };
const defaultLedgerOption: ReportSelectOption = { label: 'Select Ledger', value: '' };

interface CostBreakupRow {
    costCenterId: number | null;
    costCenterName: string;
    openingDr: number;
    openingCr: number;
    transactionsDr: number;
    transactionsCr: number;
    closingDr: number;
    closingCr: number;
}

interface CostBreakupHeader {
    ledger: {
        id: number;
        code: string;
        name: string;
    };
    projectName?: string | null;
    fromDate: string;
    toDate: string;
}

const formatBalance = (dr: number, cr: number): string => {
    const debit = Number(dr || 0);
    const credit = Number(cr || 0);
    const diff = debit - credit;

    if (Math.abs(diff) < 0.0001) {
        return '0.00';
    }

    if (diff > 0) {
        return `${numberFormat(diff)} Dr`;
    }

    return `${numberFormat(Math.abs(diff))} Cr`;
};

const CostBreakupReport: React.FC = () => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<CostBreakupRow[]>([]);
    const [headerInfo, setHeaderInfo] = useState<CostBreakupHeader | null>(null);

    const [projects, setProjects] = useState<ReportSelectOption[]>([]);
    const [project, setProject] = useState<ReportSelectOption>(defaultProjectOption);

    const [ledgerOptions, setLedgerOptions] = useState<ReportSelectOption[]>([]);
    const [ledger, setLedger] = useState<ReportSelectOption>(defaultLedgerOption);

    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        idAccLedgers: query.get('idAccLedgers') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    const hasInitialQuery = useMemo(
        () =>
            Boolean(
                query.get('idProjects') && query.get('idAccLedgers') && query.get('fromDate') && query.get('toDate')
            ),
        [query]
    );

    const autoLoadTriggeredRef = useRef(false);
    const initialLedgerIdRef = useRef<string | null>(query.get('idAccLedgers'));

    const fetchProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions: ReportSelectOption[] = (resp?.data || []).map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));

                setProjects(projectOptions);

                const queryProjectId = query.get('idProjects');
                let nextProject = defaultProjectOption;

                if (queryProjectId) {
                    nextProject =
                        projectOptions.find((opt) => String(opt.value) === String(queryProjectId)) ?? defaultProjectOption;
                } else if (projectOptions.length > 0) {
                    nextProject = projectOptions[0];
                }

                setProject(nextProject);
                setFormData((prev) => ({
                    ...prev,
                    idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
                }));
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const fetchLedgerOptions = useCallback(async (projectId: string, searchTerm = ''): Promise<ReportSelectOption[]> => {
        if (!projectId) {
            return [];
        }

        const params = new URLSearchParams({
            type: 'journal',
            idProjects: projectId,
        });

        if (searchTerm) {
            params.append('q', searchTerm);
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}transaction/ledgers?${params.toString()}`,
                getRequestOptions()
            );
            const resp = await response.json();

            const options: ReportSelectOption[] = (resp?.data || []).map((ledgerItem: any) => ({
                label: `${ledgerItem.ledgerCode} ${ledgerItem.ledgerName}`.trim(),
                value: ledgerItem.idAccLedgers,
                path: ledgerItem.ledgerPath,
            }));

            return options;
        } catch (error) {
            console.log(error, 'ledger fetch error');
            return [];
        }
    }, []);

    const loadLedgerOptions = useCallback(
        async (inputValue: string) => {
            const projectId = project.value ? String(project.value) : '';
            if (!projectId) {
                return [];
            }

            return fetchLedgerOptions(projectId, inputValue);
        },
        [project.value, fetchLedgerOptions]
    );
 
    useEffect(() => {
        const projectId = project.value ? String(project.value) : '';

        if (!projectId) {
            setLedgerOptions([]);
            setLedger(defaultLedgerOption);
            setFormData((prev) => ({ ...prev, idAccLedgers: '' }));
            return;
        }

        let isMounted = true;

        fetchLedgerOptions(projectId)
            .then((options) => {
                if (!isMounted) {
                    return;
                }

                setLedgerOptions(options);

                let nextLedger = defaultLedgerOption;
                if (initialLedgerIdRef.current) {
                    nextLedger =
                        options.find((opt) => String(opt.value) === String(initialLedgerIdRef.current)) ?? defaultLedgerOption;
                } else if (options.length === 1) {
                    nextLedger = options[0];
                }

                setLedger(nextLedger);
                setFormData((prev) => ({
                    ...prev,
                    idAccLedgers: nextLedger.value !== '' ? String(nextLedger.value) : '',
                }));

                initialLedgerIdRef.current = null;
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setLedgerOptions([]);
                setLedger(defaultLedgerOption);
                setFormData((prev) => ({ ...prev, idAccLedgers: '' }));
                initialLedgerIdRef.current = null;
            });

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project.value, fetchLedgerOptions]);

    const handleProjectChange = (selectedProject: ReportSelectOption | null) => {
        const nextProject = selectedProject ?? defaultProjectOption;
        setProject(nextProject);
        setFormData((prev) => ({
            ...prev,
            idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
            idAccLedgers: '',
        }));
        setLedger(defaultLedgerOption);
        setLedgerOptions([]);
    };

    const handleLedgerChange = (selectedLedger: ReportSelectOption | null) => {
        const nextLedger = selectedLedger ?? defaultLedgerOption;
        setLedger(nextLedger);
        setFormData((prev) => ({
            ...prev,
            idAccLedgers: nextLedger.value !== '' ? String(nextLedger.value) : '',
        }));
    };

    const queryParams = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        return params;
    }, [formData]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = useCallback(async () => {
        if (!formData.idProjects) {
            toast.error('Project is required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.idAccLedgers) {
            toast.error('Ledger is required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('From Date and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        setRows([]);
        setHeaderInfo(null);

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/cost-breakup',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                const payload = data.data || {};

                const mappedRows: CostBreakupRow[] = (payload.rows || []).map((row: any) => ({
                    costCenterId: row.costCenterId ?? null,
                    costCenterName: row.costCenterName ?? 'Unassigned',
                    openingDr: Number(row.openingDr ?? 0),
                    openingCr: Number(row.openingCr ?? 0),
                    transactionsDr: Number(row.transactionsDr ?? 0),
                    transactionsCr: Number(row.transactionsCr ?? 0),
                    closingDr: Number(row.closingDr ?? 0),
                    closingCr: Number(row.closingCr ?? 0),
                }));

                setRows(mappedRows);

                if (payload.header) {
                    setHeaderInfo({
                        ledger: {
                            id: Number(payload.header.ledger?.id ?? 0),
                            code: payload.header.ledger?.code ?? '',
                            name: payload.header.ledger?.name ?? '',
                        },
                        projectName: payload.header.projectName ?? '',
                        fromDate: payload.header.fromDate ?? formData.fromDate,
                        toDate: payload.header.toDate ?? formData.toDate,
                    });
                } else {
                    setHeaderInfo(null);
                }
            } else {
                setRows([]);
                setHeaderInfo(null);

                if (data?.errorMessage) {
                    toast.error(data.errorMessage, { position: 'bottom-right', autoClose: 4000 });
                }

                (data?.errors || []).forEach((error: string) =>
                    toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Failed to load report. Please try again later.', {
                position: 'bottom-right',
                autoClose: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, [formData]);

    useEffect(() => {
        if (
            hasInitialQuery &&
            !autoLoadTriggeredRef.current &&
            formData.idProjects &&
            formData.idAccLedgers &&
            formData.fromDate &&
            formData.toDate
        ) {
            handleViewReport();
            autoLoadTriggeredRef.current = true;
        }
    }, [formData.idProjects, formData.idAccLedgers, formData.fromDate, formData.toDate, hasInitialQuery, handleViewReport]);

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="ledger">
                                    <Form.Label>Ledger<span className='required'></span></Form.Label>
                                    <AsyncSelect
                                        cacheOptions
                                        defaultOptions={ledgerOptions}
                                        loadOptions={loadLedgerOptions}
                                        value={ledger && ledger.value !== '' ? ledger : null}
                                        onChange={handleLedgerChange as any}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                        placeholder="Select Ledger"
                                        formatOptionLabel={(option: any) => (
                                            <div>
                                                <div>{option.label}</div>
                                                {option.path && (
                                                    <small className='text-muted'>{option.path}</small>
                                                )}
                                            </div>
                                        )}
                                        components={{
                                            SingleValue: ({ data, ...rest }: any) => (
                                                <components.SingleValue {...rest}>
                                                    <div>
                                                        <div>{data.label}</div>
                                                        {data.path && (
                                                            <small className='text-muted'>{data.path}</small>
                                                        )}
                                                    </div>
                                                </components.SingleValue>
                                            ),
                                        }}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="From Date"
                                        name='fromDate'
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name='toDate'
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-primary w-100 p-3'
                                        onClick={handleViewReport}
                                        disabled={loading}
                                    >
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <a
                                        className="btn btn-sm btn-primary w-100 p-3"
                                        href={`${process.env.REACT_APP_BACKEND}cost_breakup_report_download?${queryParams.toString()}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <i className='fas fa-download me-1'></i>
                                        Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            {headerInfo && (
                <div className='mb-4 text-center'>
                    <h5 className='fw-bold mb-1'>Project: {headerInfo.projectName}</h5>
                    <div><strong>Ledger:</strong> {headerInfo.ledger.code} {headerInfo.ledger.name}</div>
                    <div><strong>Period:</strong> {headerInfo.fromDate} to {headerInfo.toDate}</div>
                </div>
            )}

            <div className='row'>
                <div className='col-12 table-responsive'>
                    <Table bordered hover>
                        <thead className="sticky-top bg-white" style={{ zIndex: 0 }}>
                            <tr>
                                <th>Particulars</th>
                                <th className='text-end'>Opening Balance</th>
                                <th className='text-end'>Transactions (Dr)</th>
                                <th className='text-end'>Transactions (Cr)</th>
                                <th className='text-end'>Closing Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center">No Data Found</td>
                                </tr>
                            )}
                            {rows.map((row, index) => (
                                <tr key={row.costCenterId ?? `row-${index}`}>
                                    <td>{row.costCenterName}</td>
                                    <td className='text-end'>{formatBalance(row.openingDr, row.openingCr)}</td>
                                    <td className='text-end'>{numberFormat(row.transactionsDr || 0)}</td>
                                    <td className='text-end'>{numberFormat(row.transactionsCr || 0)}</td>
                                    <td className='text-end'>{formatBalance(row.closingDr, row.closingCr)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        </>
    );
};

export default CostBreakupReport;